import React, { useEffect, useState } from 'react';
import Modal from '../../../BaseComponents/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import {
  changeTestResultsModalVisibility,
  setAppointmentsForTestResultsList,
  setSelectedAppointmentIdForTestResults
} from '../../../../store/memberSlice';
import { Formik } from 'formik';
import {
  AppointmentLoadStrategy,
  AppScenariosBiomarkerGetResultsByAppointmentApplicationPayloadBiomarkerWithResultDTO,
} from '../../../../types/apiGeneratedTypes';
import TestResultsModalForm from './TestResultsModalForm';
import { AppointmentFilter } from '../../../../types/apiGeneratedTypes';
import { getAppointmentsList } from '../../../../api/appointments';
import Loader from '../../../BaseComponents/Loader';
import { TestResultsModalFormTypes } from './TestResultsModalForm';
import { addTestResults, updateTestResults } from '../../../../api/testResults';
import {
  changeUpdateBiomarkersResultsModalVisibility,
  setAlertsCount
} from '../../../../store/alertsSlice';
import { setNotesCount } from '../../../../store/appSlice';
import { getUnprocessedTestResultsCount } from '../../../../api/testResults';
import { getNotesCount } from '../../../../api/counters';

interface TestResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberName?: string;
  mode: 'create' | 'update';
  selectedAppointmentIdForTestResults?: number;
  userId?: string;
  testResultsData?: AppScenariosBiomarkerGetResultsByAppointmentApplicationPayloadBiomarkerWithResultDTO[];
}

const TestResultsModal: React.FC<TestResultsModalProps> = ({
  isOpen,
  onClose,
  memberName,
  mode,
  selectedAppointmentIdForTestResults,
  userId,
  testResultsData
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isFormError, setIsFormError] = useState<any>(null);

  const { memberData } = useSelector((state: RootState) => state.member);
  const { biomarkersList } = useSelector((state: RootState) => state.app);

  const refreshAlertsCount = async () => {
    try {
      const alerts = await getUnprocessedTestResultsCount();
      dispatch(setAlertsCount(alerts.count));
    } catch (error) {
      console.log('Error refreshing alerts count', error);
    }
  };

  const refreshNotesCount = async () => {
    try {
      const notes = await getNotesCount();
      dispatch(setNotesCount(notes.total_count));
    } catch (error) {
      console.log('Error refreshing notes count', error);
    }
  };

  const onModalClose = () => {
    dispatch(changeTestResultsModalVisibility(false));
    dispatch(setSelectedAppointmentIdForTestResults(null));
    onClose();
  };

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        setIsLoading(true);
        const appointments = await getAppointmentsList(
          1000,
          0,
          userId || memberData?.user_id!,
          AppointmentLoadStrategy.ALL_DATA,
          AppointmentFilter.PastWithToday
        );
        dispatch(setAppointmentsForTestResultsList(appointments.appointments));
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) fetchAppointments();
  }, [dispatch, isOpen, memberData, userId]);

  const getInitialValues = () => {
    return {
      appointment: { key: '', label: '' },
      biomarkers:
        mode === 'create'
          ? [
              {
                id: '',
                name: { key: '', value: '' },
                summary: '',
                value: '',
                measurement_unit: ''
              }
            ]
          : testResultsData
            ? testResultsData.map((result) => {
                return {
                  id: result.result.id,
                  name: {
                    key: result.biomarker.id,
                    value: result.biomarker.name,
                    type: result.biomarker.biomarker_type
                  },
                  summary: result.result.summary,
                  value: result.result.value,
                  measurement_unit: result.biomarker.measurement_unit,
                  biomarker_result_id: result.result.id
                };
              })
            : []
    };
  };

  
  const calculateDerivedBiomarkerValues = (biomarkers: TestResultsModalFormTypes['biomarkers']) => {
    const updatedBiomarkers = [...biomarkers];
    const valueByBiomarkerId = new Map<number, number>();

    updatedBiomarkers.forEach((biomarker) => {
      const id = parseInt(biomarker.name.key, 10);
      const parsed = parseFloat(biomarker.value);
      if (!Number.isNaN(id) && biomarker.value !== '' && !Number.isNaN(parsed)) {
        valueByBiomarkerId.set(id, parsed);
      }
    });

    updatedBiomarkers.forEach((biomarker, index) => {
      const fullBiomarker = biomarkersList?.find(
        (b: any) => b.id === parseInt(biomarker.name.key, 10)
      );

      if (!fullBiomarker?.is_derived) {
        return;
      }

      const formulaType =
        fullBiomarker.formula_type ||
        (fullBiomarker.formula_terms?.length ? 'AFFINE' : 'RATIO');

      if (formulaType === 'AFFINE') {
        const terms = fullBiomarker.formula_terms || [];
        if (!terms.length) {
          return;
        }

        let total =
          fullBiomarker.formula_constant != null
            ? Number(fullBiomarker.formula_constant)
            : 0;

        for (const term of terms) {
          const termValue = valueByBiomarkerId.get(term.biomarker_id);
          if (termValue == null) {
            return;
          }
          total += Number(term.coefficient) * termValue;
        }

        updatedBiomarkers[index].value = parseFloat(total.toFixed(4)).toString();
        return;
      }

      const numeratorId =
        fullBiomarker.numerator_biomarker_id ?? fullBiomarker.numerator_biomarker?.id;
      const denominatorId =
        fullBiomarker.denominator_biomarker_id ?? fullBiomarker.denominator_biomarker?.id;

      if (!numeratorId || !denominatorId) {
        return;
      }

      const numeratorValue = valueByBiomarkerId.get(numeratorId);
      const denominatorValue = valueByBiomarkerId.get(denominatorId);

      if (numeratorValue == null || denominatorValue == null || denominatorValue === 0) {
        return;
      }

      let num = numeratorValue;
      let den = denominatorValue;
      const scaleTarget = fullBiomarker.scale_target;
      const scaleFactor =
        fullBiomarker.scale_factor != null ? Number(fullBiomarker.scale_factor) : null;

      if (scaleTarget && scaleFactor != null) {
        if (scaleTarget === 'NUMERATOR') {
          num *= scaleFactor;
        } else if (scaleTarget === 'DENOMINATOR') {
          den *= scaleFactor;
        }
      }

      if (den === 0) {
        return;
      }

      let rawValue = num / den;
      if (scaleTarget === 'VALUE' && scaleFactor != null) {
        rawValue *= scaleFactor;
      }

      updatedBiomarkers[index].value = Math.abs(parseFloat(rawValue.toFixed(4))).toString();
    });

    return updatedBiomarkers;
  };

  const handleSubmit = async (values: TestResultsModalFormTypes) => {
    setIsSubmitting(true);

    try {
      
      const biomarkersWithCalculatedValues = calculateDerivedBiomarkerValues(values.biomarkers);
      
      let response;
      if (mode === 'create') {
        const requestObj: any = {
          appointment_id: values.appointment.key,
          member_id: memberData?.user_id!,
          biomarkers: biomarkersWithCalculatedValues.map((biomarker) => ({
            value: biomarker.value.toString(),
            biomarker_id: biomarker.name.key,
            summary: biomarker.summary,
            unit: biomarker.measurement_unit || ''
          }))
        };
        response = await addTestResults(requestObj);
      } else {
        const requestObj: any = {
          biomarkers: biomarkersWithCalculatedValues.map((biomarker) => ({
            value: biomarker.value.toString(),
            biomarker_result_id: biomarker.biomarker_result_id,
            summary: biomarker.summary,
            unit: biomarker.measurement_unit || ''
          }))
        };
        response = await updateTestResults(requestObj);
      }

      if (response) {
        dispatch(changeTestResultsModalVisibility(false));
        dispatch(setSelectedAppointmentIdForTestResults(null));
        dispatch(changeUpdateBiomarkersResultsModalVisibility(false));
        
        // Refresh both counts after creating/updating biomarker results
        await Promise.all([refreshAlertsCount(), refreshNotesCount()]);
      }
    } catch (error) {
      console.error(error);
      setIsFormError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const titleText =
    mode === 'create' ? 'Add biomarker results' : 'Update biomarker results';

  return (
    <Formik
      initialValues={getInitialValues()!}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      <Modal
        isOpen={isOpen}
        onClose={onModalClose}
        title={titleText}
        memberName={memberName}
        width={'800px'}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <TestResultsModalForm
            isSubmitting={isSubmitting}
            isFormError={isFormError}
            isOpen={isOpen}
            mode={mode}
            selectedAppointmentIdForTestResults={
              selectedAppointmentIdForTestResults
            }
          />
        )}
      </Modal>
    </Formik>
  );
};

export default TestResultsModal;
